import React, { useEffect, useState } from 'react';
import Scheduler from 'devextreme-react/scheduler';
import { Box, Typography } from '@mui/material';
import { fetchWorkcellActivity, fetchDailyHoursSummary } from '../api';
import 'devextreme/dist/css/dx.light.css';
import './WorkcellDailyCalendar.css';
import { useTranslation } from 'react-i18next';
import { formatSchedulerTime, parseSchedulerDateTime } from '../utils/localDateTime';

const getActivityColor = (percentage) => {
    if (percentage <= 50) return 'rgb(255, 0, 0)';
    if (percentage >= 95) return 'rgb(0, 255, 0)';
    const factor = (percentage - 50) / 45;
    if (factor < 0.5) {
        const orangeFactor = factor * 2;
        return `rgb(255, ${Math.round(165 * orangeFactor)}, 0)`;
    } else {
        const greenFactor = (factor - 0.5) * 2;
        return `rgb(${Math.round(255 * (1 - greenFactor))}, ${Math.round(165 + (255 - 165) * greenFactor)}, 0)`;
    }
};

const WorkcellDailyCalendar = ({ workcellId, date }) => {
    const [activities, setActivities] = useState([]);
    const [currentView, setCurrentView] = useState('timelineDay');
    const { t } = useTranslation();

    const formatTime = (dt) => {
        if (!dt) return t('common.na');
        return formatSchedulerTime(dt) || t('common.na');
    };

    const handleAppointmentFormOpening = (e) => {
        const timeslotIdsText = Array.isArray(e.appointmentData.timeslotIds) && e.appointmentData.timeslotIds.length > 0
            ? e.appointmentData.timeslotIds.join(', ')
            : t('common.na');

        const formData = {
            ...e.appointmentData,
            timeslotIdsText,
        };

        e.form.option('formData', formData);

        const items = [...(e.form.option('items') || [])];
        const timeslotItem = {
            dataField: 'timeslotIdsText',
            editorType: 'dxTextArea',
            colSpan: 2,
            label: { text: t('daily.workcellSchedule.tooltip.timeslotIds') },
            editorOptions: {
                readOnly: true,
                autoResizeEnabled: true,
                minHeight: 80,
                maxHeight: 180,
            },
        };

        const mainGroup = items.find((item) => item.name === 'mainGroup');
        if (mainGroup && Array.isArray(mainGroup.items)) {
            mainGroup.items = [
                ...mainGroup.items.filter((item) => item.dataField !== 'timeslotIdsText'),
                timeslotItem,
            ];
        } else {
            items.push(timeslotItem);
        }

        e.form.option('items', items);
    };

    useEffect(() => {
        const loadData = async () => {
            try {
                const activityData = await fetchWorkcellActivity(date);
                const summaryData = await fetchDailyHoursSummary(date);
                const summaryMap = {};
                summaryData.forEach(summary => {
                    if (summary.workcell_id === parseInt(workcellId)) {
                        summaryMap[summary.workcell_id] = summary.activity_percentage;
                    }
                });
                const filtered = (activityData || []).filter(a => (a.has_oee === true || a.has_oee === 1) && a.workcell_id === parseInt(workcellId));
                const transformed = filtered.map((activity, idx) => {
                    const percentage = summaryMap[activity.workcell_id] || 0;
                    return {
                        id: idx,
                        text: activity.workcell_name,
                        startDate: parseSchedulerDateTime(activity.from_time),
                        endDate: parseSchedulerDateTime(activity.to_time),
                        workcellId: activity.workcell_id,
                        timeslotIds: Array.isArray(activity.timeslot_ids) ? activity.timeslot_ids : [],
                        color: getActivityColor(percentage)
                    };
                });
                setActivities(transformed);
            } catch (error) {
                setActivities([]);
            }
        };
        if (workcellId && date) loadData();
    }, [workcellId, date]);

    return (
        <Box mt={5} className="workcell-calendar">
            <Typography variant="h4" gutterBottom>{t('daily.workcellSchedule.calendarTitle')}</Typography>
            <Scheduler
                dataSource={activities}
                views={['day', 'timelineDay']}
                currentView={currentView}
                onCurrentViewChange={setCurrentView}
                currentDate={parseSchedulerDateTime(`${date}T00:00:00`)}
                timeZone="UTC"
                width="100%"
                groups={[]}
                startDayHour={6}
                endDayHour={20}
                resourceCellRender={undefined}
                editing={{ allowAdding: false, allowUpdating: false, allowDeleting: false }}
                                onAppointmentFormOpening={handleAppointmentFormOpening}
                resources={[]}
                appointmentRender={model => (
                  <div style={{
                    backgroundColor: model.appointmentData.color,
                    height: '100%',
                    width: '100%',
                    border: 'none',
                    borderRadius: 0,
                    margin: 0
                  }} />
                )}
                appointmentTooltipRender={model => (
                                    <div>
                    <strong>{model.appointmentData.text}</strong>
                    <div>
                        {t('daily.workcellSchedule.tooltip.duration')}: {formatTime(model.appointmentData.startDate)} — {formatTime(model.appointmentData.endDate)}
                    </div>
                  </div>
                )}
                onAppointmentRendered={e => {
                    const parent = e.appointmentElement.parentElement;
                    if (parent) {
                        parent.style.backgroundColor = 'transparent';
                        parent.style.border = 'none';
                    }
                }}
            />
        </Box>
    );
};

export default WorkcellDailyCalendar;
